<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_orders')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <button
            class="action-btn export-btn"
            @click="exportModal.show = true"
          >
            <v-icon name="file-download"></v-icon>
            {{ $t('globals.export').toUpperCase() }}
          </button>
        </div>
      </div>
      <div class="tab-content-inner">
            <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.search") }}</label>
        <b-form-input
          v-model="searchQuery.customerName"
          @keyup.enter="selectFetch"
          class="search-input"
          :placeholder="$t('globals.customer') + '...'"
          :disabled="getAllOrdersLoading"
        />
      </div>

      <div class="filter-group">
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.status") }}</label>
          <b-form-select
            v-model="searchQuery.status"
            :options="statusOptions"
            class="filter-select"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.order_no") }}</label>
          <b-form-input
            v-model="searchQuery.orderNo"
            @keyup.enter="selectFetch"
            class="filter-select"
            :disabled="getAllOrdersLoading"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.agent") }}</label>
          <b-form-input
            v-model="searchQuery.agentName"
            @keyup.enter="selectFetch"
            class="filter-select"
            :disabled="getAllOrdersLoading"
          />
        </div>
      </div>

      <div class="action-buttons">
        <b-button
          variant="primary"
          class="search-btn"
          @click="selectFetch"
        >
          {{ $t("globals.search").toUpperCase() }}
        </b-button>
        <button
          class="reload-btn"
          @click="resetReload"
          :disabled="getAllOrdersLoading"
          v-b-tooltip.hover
          :title="$t('globals.clear_filters')"
        >
          <icon name="sync-alt" :spin="getAllOrdersLoading"></icon>
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper" style="position: relative; min-height: 300px;">
      <!-- Loading Overlay -->
      <div v-if="getAllOrdersLoading" class="loading-overlay">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>

      <!-- Table Content -->
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key"
              :class="{ sortable: field.sortable }"
              @click="field.sortable && handleSort(field.key)"
            >
              {{ field.label }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Empty State Row -->
          <tr v-if="!getAllOrdersLoading && getAllOrders.length === 0">
            <td :colspan="fields.length" class="empty-state-cell">
              {{ $t("globals.no_results") }}
            </td>
          </tr>

          <!-- Data Rows -->
          <tr
            v-for="item in getAllOrders"
            :key="item.id"
            class="table-row"
            @click="rowClicked(item)"
          >
            <td>{{ item.order_no }}</td>
            <td>{{ item.agent ? item.agent.full_name : '-' }}</td>
            <td>
              <router-link
                v-if="item.customer"
                class="customer-link"
                :to="{name: 'customer', params:{ id: item.customer.id }}"
                @click.native.stop
                target="_blank"
              >
                {{ item.customer.company_name }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td>
              <span class="status-badge" :class="`status-${item.status}`">
                {{ $t('globals.' + item.status) }}
              </span>
              <span v-if="item.cancell_at" class="status-badge status-cancelled ml-2">
                {{ `${$t("globals.cancel_at")}: ${item.cancell_at}` }}
              </span>
            </td>
            <td v-b-tooltip.hover :title="item.created_at">
              {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
            </td>
            <td>{{ item.number_of_devices != null ? item.number_of_devices : 0 }}</td>
            <td>
              <div
                v-if="item.payment_type == 'mollie_direct_debit'"
                style="margin-top: -10px; margin-bottom: -10px"
              >
                <div v-if="item.customer && item.customer.mollie_order_static">
                  <img style="height: 45px" src="@/assets/mollie-vector-logo.svg"/>
                </div>
                <div v-else>
                  <img style="height: 45px" src="@/assets/mollie-vector-logo-red.png"/>
                </div>
              </div>
              <div v-else>
                {{ item.payment_type }}
              </div>
            </td>
            <td>{{ item.contract_start_on }}</td>
            <td v-if="item.status == 'completed'" style="text-align: right;">
              {{ currencyFormat(item.first_payment_value, item.currency) }}
            </td>
            <td v-else>-</td>
            <td style="text-align: right;">
              {{ currencyFormat(item.total_price_with_vat, item.currency) }}
            </td>
            <td v-b-tooltip.hover :title="item.notes">
              {{
                item.notes && item.notes.length > 20
                  ? item.notes.substring(0, 10) + "..."
                  : item.notes || '-'
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + getAllOrders.length) }}</span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="offset === 0 || getAllOrdersLoading"
          @click="fetchLess"
        >
          &lt;
        </button>
        <button
          class="page-btn"
          :disabled="getAllOrders.length < limit || getAllOrdersLoading"
          @click="fetchMore"
        >
          &gt;
        </button>
      </div>
    </div>
      </div>
    </div>

    <!-- Modals -->
    <ulu-orders-export-modal
      :show="exportModal.show"
      :close="closeModals"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { currencyFormat } from "@/helpers/price";

import "vue-awesome/icons/sync-alt";
import "vue-awesome/icons/file-download";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-orders-export-modal": () =>
      import("@/components/orders/all_orders/OrdersExportModal.vue")
  },
  data() {
    return {
      exportModal: {
        show: false
      },
      statusOptions: [
        { text: this.$t('globals.all'), value: null },
        { text: this.$t('globals.created'), value: 'created' },
        { text: this.$t('globals.completed'), value: 'completed' },
        { text: this.$t('globals.canceled'), value: 'canceled' },
        { text: this.$t('globals.terminated'), value: 'terminated' },
      ],
      searchQuery: {
        agentName: null,
        orderNo: null,
        customerName: null,
        status: null,
      },
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        {
          key: "order_no",
          label: this.$t("globals.order_no"),
          sortable: true
        },
        {
          key: "agent.full_name",
          label: this.$t("globals.agent"),
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
        },
        {
          key: "created_at",
          label: this.$t("globals.creation_date"),
          sortable: true
        },
        {
          key: "number_of_devices",
          label: this.$t("globals.number_of_devices"),
        },
        {
          key: "payment_type",
          label: this.$t("globals.payment_type"),
          sortable: true
        },
        {
          key: "contract_start_on",
          label: this.$t("globals.start_date"),
          sortable: true
        },
        {
          key: "first_payment_value",
          label: this.$t("globals.first_payment"),
        },
        {
          key: "total_price_with_vat",
          label: this.$t("globals.total_value"),
          sortable: true
        },
        {
          key: "notes",
          label: this.$t("globals.notes"),
        },
      ]
    };
  },
  computed: {
    ...mapGetters("orders", ["getAllOrders", "getAllOrdersLoading"]),
  },
  methods: {
    currencyFormat,
    ...mapActions("orders", [
      "fetchOrdersPagination",
      "fetchOrderOrderNo",
      "fetchOrdersLikeAgent",
      "fetchOrdersLikeCustomer"
    ]),
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "order", params: { id: item.id } });
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
      this.fetch();
    },
    fetchMore() {
      this.offset += 20;
      this.selectFetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.selectFetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.agentName = null;
      this.searchQuery.orderNo = null;
      this.searchQuery.customerName = null;
      this.searchQuery.status = null;
    },
    fetch() {
      let order_by = this.sortBy;
      // Customer exception
      if (order_by === 'customer') {
        order_by = 'customer_id';
      }

      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchOrdersPagination({
        this_ref: this,
        status: this.searchQuery.status,
        limit: this.limit,
        offset: this.offset,
        order: order_by
      });
    },
    selectFetch() {
      if (
        this.searchQuery.orderNo != null
        && this.searchQuery.orderNo.trim().length > 0
      ) {
        this.searchOrdersOrderNo();
      } else if (
        this.searchQuery.agentName != null
        && this.searchQuery.agentName.trim().length > 2
      ) {
        this.searchOrdersLikeAgentName();
      } else if (
        this.searchQuery.customerName != null
        && this.searchQuery.customerName.trim().length > 2
      ) {
        this.searchOrdersLikeCustomerName();
      } else {
        this.fetch();
      }
    },
    searchOrdersOrderNo() {
      if (this.searchQuery.orderNo != null) {
        this.searchQuery.agentName = null;
        this.searchQuery.customerName = null;

        this.resetOffsetSort();
        this.fetchOrderOrderNo({
          orderNo: this.searchQuery.orderNo,
          this_ref: this
        });
      }
    },
    searchOrdersLikeAgentName() {
      if (this.searchQuery.agentName != null && this.searchQuery.agentName.length > 2) {
        this.searchQuery.orderNo = null;
        this.searchQuery.customerName = null;

        this.fetchOrdersLikeAgent({
          agentName: this.searchQuery.agentName,
          status: this.searchQuery.status,
          this_ref: this,
          limit: this.limit,
          offset: this.offset,
        });
      }
    },
    searchOrdersLikeCustomerName() {
      if (this.searchQuery.customerName != null && this.searchQuery.customerName.length > 2) {
        this.searchQuery.orderNo = null;
        this.searchQuery.agentName = null;

        this.fetchOrdersLikeCustomer({
          customerName: this.searchQuery.customerName,
          status: this.searchQuery.status,
          this_ref: this,
          limit: this.limit,
          offset: this.offset,
        });
      }
    },
    closeModals() {
      this.exportModal.show = false;
      this.resetReload();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    }
  },
  watch: {
    'searchQuery.agentName'(newVal) {
      if (newVal) {
        this.searchQuery.orderNo = null;
        this.searchQuery.customerName = null;
        this.resetOffsetSort();
      }
    },
    'searchQuery.orderNo'(newVal) {
      if (newVal) {
        this.searchQuery.agentName = null;
        this.searchQuery.customerName = null;
        this.resetOffsetSort();
      }
    },
    'searchQuery.customerName'(newVal) {
      if (newVal) {
        this.searchQuery.agentName = null;
        this.searchQuery.orderNo = null;
        this.resetOffsetSort();
      }
    },
    'searchQuery.status'(newVal) {
      this.resetOffsetSort();
      this.selectFetch();
    }
  },
  mounted() {
    this.resetReload();
  }
};
</script>
